'use client';

import React from 'react';

// Dynamic import of RecordRTC
import type RecordRTC from 'recordrtc';
let RecordRTCModule: typeof RecordRTC | null = null;

// eslint-disable-next-line
let isModuleLoading = true;

if (isBrowser()) {
  import('recordrtc')
    .then((module) => {
      RecordRTCModule = module.default;
      isModuleLoading = false;
    })
    .catch((err) => {
      console.error('Failed to load RecordRTC:', err);
      isModuleLoading = false;
    });
}

import { format } from 'date-fns';
import { useState, useEffect } from 'react';
import DebugVideo from './DebugVideo';
import pianola from '../../entities/Pianola/Pianola';
import './Debug.css';
import { observer } from 'mobx-react';
import samplesSelectorModel from '../Samples/SamplesSelector/SamplesSelectorModel';
import DebugModelEntity from './DebugModel';
import { apiSwagger } from '../../shared/api';
import EventsSender from './EventsSender';
import { ManagerEntity } from '@/shared/globalFunctions';
import { dpAssert, isDebugPage, midiToScientificPitch } from '@/sharedTypes/helpers';
import { gMsg, notifyStatus } from '@/sharedTypes/coreTypes';
import { triggerEvent } from '@/sharedTypes/pureHelpers';
import { isBrowser } from '@/sharedTypes/browserUtils';
import { usePathname } from 'next/navigation';
import ShowDevTextCheckbox from '../Options/ShowDevTextCheckbox/ShowDevTextCheckbox';

const Debug = observer(() => {
  const [showVideo, setShowVideo] = useState<boolean>(true);
  const pathname = usePathname();

  const selectedSample = samplesSelectorModel.selectedSample;

  const displayDatas = DebugModelEntity.displayData;

  const [rewindValue, setRewindValue] = useState<number>(0);

  const [selectedData, setSelectedData] = useState<any | null>(null);

  const selectedUUID = ManagerEntity.selectedDebug;
  const setSelectedUUID = (uuid: string) => {
    ManagerEntity.selectedDebug = uuid;
  };

  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);

  let showDebugPlayback = false;

  // you can set DEBUG=true in .env file and debug will work
  const showDebugRollup: any = '__SHOW_DEBUG__';
  if (showDebugRollup === 'true') {
    showDebugPlayback = true;
  }

  if (pathname === '/debug' || pathname === '/debug/') {
    showDebugPlayback = true;
  }

  useEffect(() => {
    if (isDebugPage()) {
      refreshUUIDS();
    }
  }, []);

  const handleSelectChange = async (event: any) => {
    setSelectedUUID(event.target.value);

    // set debug data to player
    const debugData = await getDebugData(event.target.value);

    dpAssert(debugData);
    setSelectedData(debugData);

    // change selected sample if it's differs from sample from debug data
    const debugSampleIdentifier: string | undefined = debugData.sample;
    if (
      debugSampleIdentifier &&
      debugSampleIdentifier != '' &&
      debugSampleIdentifier != selectedSample?.identifier
    ) {
      await samplesSelectorModel.selectSample(debugSampleIdentifier);
    }

    if (ManagerEntity.player) {
      if (debugData.practiceModeId === undefined) {
        debugData.practiceModeId = 0;
        ManagerEntity.gMsg = new gMsg(
          'Debug data does not have practiceModeId',
          notifyStatus.failed
        );
      }
      const practiceModeForDebug = ManagerEntity.practiceModes.find(
        (p: any) => p.id === debugData.practiceModeId
      );
      dpAssert(practiceModeForDebug);
      ManagerEntity.player?.debug.setNewDebugData(
        debugData,
        practiceModeForDebug
      );
    }

    triggerEvent('debugLoaded');
    ManagerEntity.gMsg = new gMsg('Debug data loaded', 0);

    // set debug video
    console.log('loading video');
    try {
      const blob = await getDebugVideo(event.target.value);
      if (blob) {
        setVideoBlob(blob);
      }
    } catch (err) {
      console.error(err);
    }
  };
  const refreshUUIDS = () => {
    DebugModelEntity.refreshUUIDS();
  };

  if (!showDebugPlayback) {
    return null;
  }

  return (
    <div
      style={{
        zIndex: 999999999999999,
        pointerEvents: 'auto',
        position: 'fixed',
        top: 0,
        left: 0,
        overflow: 'visible',
        display: 'block',
        visibility: 'visible',
      }}
    >
      <div className={`Debug ${pathname === '/debug' || pathname === '/debug/' ? 'Debug--debugPage' : ''}`}>
        <div>
          {/*
              <div style={{ paddingTop: "10px" }}>
                  <div>
                      Load debug data from json
                  </div>
                  <input type="file" onChange={(e) => {
                      handleFileChange(e, props.player);
                  }} />
              </div>
              */}
        </div>
        <div>
          <div>
            <label htmlFor='uuidSelect'>Select a debug info:</label>
            <select
              id='uuidSelect'
              value={selectedUUID}
              onChange={handleSelectChange}
            >
              <option value=''>Select an UUID</option>
              {displayDatas.map((data: any, index) => {
                if (data.date && data.uuid) {
                  let username = data.username;
                  if (!username) {
                    username = 'unknown';
                  }
                  const formattedDate = format(
                    new Date(data.date),
                    'dd-MM-yyyy HH:mm:ss OOOO'
                  );
                  return (
                    <option key={index} value={data.uuid}>
                      {data.uuid} - {formattedDate} {username}
                    </option>
                  );
                }
                return null;
              })}
            </select>
            <ShowDevTextCheckbox></ShowDevTextCheckbox>

            {selectedData && (
              <div style={{ paddingTop: '10px' }}>
                <span>
                  Selected UUID:{' '}
                  {format(
                    new Date(selectedData.date),
                    'dd-MM-yyyy HH:mm:ss OOOO'
                  )}
                  {selectedData.username}
                </span>
                <div>
                  <label>
                    Auto removable
                    <input
                      type='checkbox'
                      checked={selectedData.autoRemovable}
                      onChange={async (e: any) => {
                        const newValue = e.target.checked;
                        ManagerEntity.spinnerMsg = 'Updating debug data...';
                        const response =
                          await apiSwagger.debugEvents.debugEventsUpdate(
                            selectedData.uuid,
                            {
                              autoRemovable: newValue,
                            }
                          );
                        if (response.status === 200) {
                          setSelectedData({
                            ...selectedData,
                            autoRemovable: newValue,
                          });
                        }
                        ManagerEntity.spinnerMsg = '';
                      }}
                    />
                  </label>
                </div>
                <button
                  style={{ display: 'inline-block', marginLeft: '10px' }}
                  onClick={async () => {
                    const userConfirmed = window.confirm(
                      'Are you sure you want to delete this data?'
                    );
                    if (userConfirmed) {
                      setSelectedData(null);
                      await deleteDebugData(selectedData.uuid);
                      refreshUUIDS();
                    }
                  }}
                >
                  Delete
                </button>
              </div>
            )}
          </div>
          <div style={{ display: 'block', paddingTop: '10px' }}>
            <div>
              <button
                onClick={() => {
                  if (ManagerEntity.player) {
                    ManagerEntity.player.debug.playDebug();
                  }
                }}
              >
                Play debug info
              </button>
              <button
                onClick={() => {
                  if (ManagerEntity.player) {
                    ManagerEntity.player.debug.stopDebug();
                  }
                }}
              >
                Stop play debug info
              </button>
              <button
                onClick={async () => {
                  if (pianola) {
                    await pianola.play();
                  }
                }}
              >
                Play pianola
              </button>
              <button
                onClick={async () => {
                  if (pianola) {
                    await pianola.play(true);
                  }
                }}
              >
                Play pianola with red notes
              </button>
            </div>
            <div
              style={{
                position: 'relative',
                top: '25%',
                left: '0',
                zIndex: 9999,
                background: 'white',
                padding: '10px',
                borderRadius: '5px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                display: 'flex',
                flexDirection: 'row',
                gap: '8px',
                height: '100px',
              }}
            >
              <h3
                style={{
                  margin: '0 0 8px 0',
                  fontSize: '14px',
                  fontWeight: 'bold',
                }}
              >
                Navigation Controls
              </h3>

              <div style={{ display: 'flex', gap: '5px' }}>
                <button
                  style={{
                    flex: 1,
                    padding: '8px',
                    background: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    if (ManagerEntity.player) {
                      ManagerEntity.player.debug.goToPrevDebug();
                    }
                  }}
                >
                  Go to prev
                </button>
                <button
                  style={{
                    flex: 1,
                    padding: '8px',
                    background: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    if (ManagerEntity.player) {
                      ManagerEntity.player.debug.goToNextDebug();
                    }
                  }}
                >
                  Go to next
                </button>
              </div>

              <button
                style={{
                  width: '100%',
                  padding: '8px',
                  background: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  if (ManagerEntity.player) {
                    ManagerEntity.player.debug.goToNextDebug(true);
                  }
                }}
              >
                Next With Position
              </button>

              <div
                style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
              >
                <input
                  style={{
                    flex: 1,
                    width: '50%',
                    padding: '6px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                  }}
                  value={rewindValue}
                  onChange={async (event) => {
                    setRewindValue(parseInt(event.target.value));
                  }}
                />
                <button
                  style={{
                    padding: '6px 10px',
                    background: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                  onClick={async () => {
                    ManagerEntity.player?.debug.rewind(rewindValue);
                  }}
                >
                  rewind
                </button>
              </div>

              <div
                style={{
                  margin: '5px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                }}
              >
                <input
                  id='showDebugVideo'
                  type='checkbox'
                  checked={showVideo}
                  onChange={() => {
                    setShowVideo(!showVideo);
                  }}
                  style={{ cursor: 'pointer' }}
                />
                <label
                  htmlFor='showDebugVideo'
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Show debug video
                </label>
              </div>

              <button
                style={{
                  width: '100%',
                  padding: '8px',
                  background: '#15803d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  marginTop: '5px',
                }}
                onClick={() => {
                  pianola.enabled = true;
                  pianola.pressNotes();
                }}
              >
                Press nearest notes
              </button>

               <button
                style={{
                  width: '100%',
                  padding: '8px',
                  background: 'red',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  marginTop: '5px',
                }}
                onClick={() => {
                  pianola.enabled = true;
                  pianola.pressWrongNote();
                }}
              >
                Press a wrong note
              </button>
            </div>

            <DebugVideo showVideo={showVideo} blob={videoBlob}></DebugVideo>
          </div>
          <div>
            <button
              onClick={() => {
                if (ManagerEntity.player?.debugData) {
                  const data = preprocessEventsToCsv(
                    ManagerEntity.player.debugData
                  );
                  const csv = objectsToCSV(data);
                  saveCSVFile(csv);
                  if (videoBlob) {
                    const webmFile = new File([videoBlob], 'file-name.webm', {
                      type: 'video/webm;codecs=vp9,opus', // Adjust codecs as needed
                    });
                    dpAssert(RecordRTCModule);
                    RecordRTCModule.invokeSaveAsDialog(webmFile);
                  }
                }
              }}
            >
              Download csv
            </button>
          </div>
          <div>
            <button
              onClick={() => {
                if (ManagerEntity.player?.debugData) {
                  const debugData = ManagerEntity.player?.debugData;
                  const str = JSON.stringify(debugData);
                  const blob = new Blob([str], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${debugData.uuid}.json`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }
              }}
            >
              Download json
            </button>
            <button
              style={{
                top: '35%',
                left: '0',
                padding: '8px 12px',
                background: '#ea580c',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                zIndex: 9999,
              }}
              onClick={async () => {
                await ManagerEntity.player?.executePlayModeIteration(
                  false,
                  true
                );
              }}
            >
              Go next play
            </button>
          </div>
          <EventsSender></EventsSender>
        </div>
      </div>
    </div>
  );
});

function preprocessEventsToCsv(debugEvents: { events: any }) {
  type obj = {
    Timestamp: string;
    TimestampDiff: number;
    Тип: string;
    'Номер ноты': number;
    'Название ноты': string;
    Duration: number;
    'Номер такта': number;
    Позиция: number;
    Velocity: number;
    Release: number;
  };
  const result: obj[] = [];
  let latestMeasure = 0;
  let latestOffset = 0;
  let currIndexVertical = 1;
  let prevTimestamp = null;
  for (const timeStamp in debugEvents.events) {
    const event = debugEvents.events[timeStamp];
    let timeStampDiff = 0;
    if (prevTimestamp) {
      timeStampDiff = parseInt(timeStamp) - parseInt(prevTimestamp);
    }
    if (event.position.measureIndex != latestMeasure) {
      currIndexVertical = 0;
    }
    if (event.position.measureOffset !== latestOffset) {
      currIndexVertical++;
    }
    for (const noteOn of event.noteOn) {
      const note: any = (noteOn as any).note as any;
      const objToAdd: obj = {
        Timestamp: timeStamp,
        TimestampDiff: timeStampDiff,
        Тип: 'on',
        'Номер ноты': note.number,
        'Название ноты': 'asdasd', // midiToScientificPitch(note.number),
        Duration: 0,
        'Номер такта': event.position.measureIndex,
        Позиция: currIndexVertical,
        Velocity: note.rawAttack,
        Release: note.rawRelease,
      };
      result.push(objToAdd);
    }
    for (const noteOff of event.noteOff) {
      const note: any = (noteOff as any).note as any;
      let duration = -1;
      // find noteOn event of that noteOff
      let elementFound = false;
      for (const ts in debugEvents.events) {
        if (elementFound) {
          break;
        }
        const event = debugEvents.events[ts];
        if (event) {
          if (event.noteOn) {
            for (const noteOn of event.noteOn) {
              if (noteOn.note.number === note.number) {
                duration = parseInt(timeStamp) - parseInt(ts);
                elementFound = true;
                break;
              }
            }
          }
        }
      }
      const objToAdd: obj = {
        Timestamp: timeStamp,
        TimestampDiff: timeStampDiff,
        Тип: 'off',
        'Номер ноты': note.number,
        'Название ноты': midiToScientificPitch(note.number),
        Duration: duration,
        'Номер такта': event.position.measureIndex,
        Позиция: currIndexVertical,
        Velocity: note.rawAttack,
        Release: note.rawRelease,
      };
      result.push(objToAdd);
    }
    latestMeasure = event.position.measureIndex;
    latestOffset = event.position.measureOffset;

    prevTimestamp = timeStamp;
  }
  return result;
}
function flattenObject(obj: any) {
  const result: any = {};

  function recurse(current: any, path = []) {
    for (const key in current) {
      const newPath: any = path.concat(key as any);
      if (typeof current[key] === 'object') {
        recurse(current[key], newPath);
      } else {
        result[newPath.join('.')] = current[key];
      }
    }
  }

  recurse(obj);

  return result;
}

function objectsToCSV(objects: any) {
  if (objects.length === 0) {
    return ''; // No data to convert
  }

  const flattenedObjects = objects.map(flattenObject);

  // Get unique properties from all objects
  const allProperties = [
    ...new Set(flattenedObjects.flatMap((obj: any) => Object.keys(obj))),
  ];

  // Create header row
  const headerRow = allProperties.join(',');

  // Create value rows
  const valueRows = flattenedObjects.map((obj: any) => {
    return allProperties.map((property: any) => obj[property]).join(',');
  });

  // Combine header and value rows with line breaks
  const csvContent = `${headerRow}\n${valueRows.join('\n')}`;

  return csvContent;
}

function saveCSVFile(data: any) {
  const encodedUri = encodeURI(data);
  const link = document.createElement('a');
  link.setAttribute('href', 'data:text/csv;charset=utf-8,\uFEFF' + encodedUri);
  link.setAttribute('download', 'report.csv');
  link.click();
}

async function getDebugVideo(uuid: string): Promise<Blob | null> {
  const response = await apiSwagger.debugVideo.debugVideoDetail(uuid);

  if (response.ok) {
    const blobData = await response.blob();
    return blobData;
  } else {
    console.error('Fetch error:');
    ManagerEntity.gMsg = new gMsg(
      'Error while tried to fetch video ' + response.error,
      notifyStatus.failed
    );
    return null;
  }
}
async function getDebugData(uuid: string) {
  const response = await apiSwagger.debugEvents.debugEventsDetail(uuid);
  if (response.error) {
    throw new Error(response.error.error);
  } else {
    const debugData = response.data;
    return debugData;
  }
}

async function deleteDebugData(uuid: string): Promise<boolean> {
  try {
    ManagerEntity.spinnerMsg = 'Removing debug data...';

    const response = await apiSwagger.debugEvents.debugEventsDelete(uuid);
    const response2 = await apiSwagger.debugVideo.debugVideoDelete(uuid);

    if (response.ok && response2.ok) {
      const data = await response.json();
      console.log('Data deleted successfully:', data);
      ManagerEntity.spinnerMsg = '';
      return true;
    } else {
      const errorData = await response.json();
      console.error('Error deleting data:', errorData.message);
      ManagerEntity.spinnerMsg = '';
      return false;
    }
  } catch (error) {
    console.error('Network error:', error);
    ManagerEntity.spinnerMsg = '';
    return false;
  }
}

export default Debug;
